import React from "react";
import { useSelector } from "react-redux";

import { Select } from "../components/ui";
import {
  fetchEmailCampaignsForAnalytics,
  fetchEmailCampaignStats,
  type EmailCampaignStatsRow,
  type EmailCampaignStatsStatus,
} from "../utils";
import { ownerSelector } from "../store/store";

type StatusFilter = "all" | EmailCampaignStatsStatus | "opened";

const STATUS_LABEL: Record<EmailCampaignStatsStatus, string> = {
  delivered: "Delivered",
  scheduled: "Scheduled",
  failed: "Failed",
  not_started: "Not started",
};

const statusClass: Record<EmailCampaignStatsStatus, string> = {
  delivered: "bg-[#E8F5E3] text-[#4A7C43]",
  scheduled: "bg-[#FFF3D6] text-[#A06600]",
  failed: "bg-[#FDECEC] text-[#9F3A3A]",
  not_started: "bg-[#F0F2F5] text-[#5C6578]",
};

const formatOpened = (
  opened: boolean | undefined,
  openedAt: string | Date | null | undefined
) => {
  if (!opened) return "No";
  return formatSentAt(openedAt);
};

const formatSentAt = (value: string | Date | null | undefined) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

export default function EmailCampaignAnalyticsPage() {
  const { owner, currentWorkspace, currentUser } = useSelector(ownerSelector);
  const [campaignOptions, setCampaignOptions] = React.useState<
    Array<{ value: string; label: string }>
  >([]);
  const [selectedCampaignId, setSelectedCampaignId] = React.useState("");
  const [rows, setRows] = React.useState<EmailCampaignStatsRow[]>([]);
  const [campaignName, setCampaignName] = React.useState("");
  const [loadingCampaigns, setLoadingCampaigns] = React.useState(false);
  const [loadingStats, setLoadingStats] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");

  React.useEffect(() => {
    let active = true;

    const loadCampaigns = async () => {
      if (!owner?._id && !currentWorkspace?._id) {
        setCampaignOptions([]);
        setSelectedCampaignId("");
        return;
      }

      setLoadingCampaigns(true);
      try {
        const campaigns = await fetchEmailCampaignsForAnalytics(
          owner?._id,
          currentUser?.userId,
          currentWorkspace?._id
        );
        if (!active) return;

        const options = campaigns.map((campaign) => ({
          value: String(campaign._id),
          label: campaign.campaignName || "Untitled campaign",
        }));
        setCampaignOptions(options);
        setSelectedCampaignId((prev) => {
          if (prev && options.some((opt) => opt.value === prev)) return prev;
          return options[0]?.value || "";
        });
      } catch {
        if (active) {
          setCampaignOptions([]);
          setSelectedCampaignId("");
        }
      } finally {
        if (active) setLoadingCampaigns(false);
      }
    };

    loadCampaigns();
    return () => {
      active = false;
    };
  }, [owner?._id, currentWorkspace?._id, currentUser?.userId]);

  React.useEffect(() => {
    let active = true;

    const loadStats = async () => {
      if (!selectedCampaignId || (!owner?._id && !currentWorkspace?._id)) {
        setRows([]);
        setCampaignName("");
        return;
      }

      setLoadingStats(true);
      try {
        const data = await fetchEmailCampaignStats(
          owner?._id,
          selectedCampaignId,
          currentUser?.userId,
          currentWorkspace?._id
        );
        if (!active) return;
        setCampaignName(data?.campaign?.campaignName || "");
        setRows(Array.isArray(data?.rows) ? data.rows : []);
      } catch {
        if (active) {
          setRows([]);
          setCampaignName("");
        }
      } finally {
        if (active) setLoadingStats(false);
      }
    };

    loadStats();
    return () => {
      active = false;
    };
  }, [
    selectedCampaignId,
    owner?._id,
    currentWorkspace?._id,
    currentUser?.userId,
  ]);

  const filteredRows = React.useMemo(() => {
    if (statusFilter === "all") return rows;
    if (statusFilter === "opened") {
      return rows.filter((row) => row.emailOpened);
    }
    return rows.filter((row) => row.status === statusFilter);
  }, [rows, statusFilter]);

  const summary = React.useMemo(() => {
    const counts: Record<EmailCampaignStatsStatus, number> = {
      delivered: 0,
      scheduled: 0,
      failed: 0,
      not_started: 0,
    };
    let opened = 0;
    for (const row of rows) {
      counts[row.status] += 1;
      if (row.emailOpened) opened += 1;
    }
    return { ...counts, opened };
  }, [rows]);

  const loading = loadingCampaigns || loadingStats;

  return (
    <section className="h-full overflow-y-auto bg-paper px-4 py-6 sm:px-9 sm:py-9">
      <div className="mx-auto max-w-[1600px]">
        <header className="mb-6">
          <h1 className="text-xl font-semibold tracking-tight text-dark-blue sm:text-[27px]">
            Email campaign stats
          </h1>
          <p className="mt-1 text-sm text-muted">
            Select a campaign with email steps to see delivery status per prospect.
          </p>
        </header>

        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
          <Select
            variant="compact"
            size="sm"
            ariaLabel="Select email campaign"
            value={selectedCampaignId}
            onChange={setSelectedCampaignId}
            disabled={loadingCampaigns || campaignOptions.length === 0}
            options={
              campaignOptions.length
                ? campaignOptions
                : [{ value: "", label: "No email campaigns found" }]
            }
            className="w-full min-w-0 font-semibold text-dark-blue sm:!w-auto sm:min-w-[180px] sm:max-w-md"
          />
          <Select
            variant="compact"
            size="sm"
            ariaLabel="Filter by email status"
            value={statusFilter}
            onChange={(value) => setStatusFilter(value as StatusFilter)}
            options={[
              { value: "all", label: "All statuses" },
              { value: "delivered", label: "Delivered" },
              { value: "scheduled", label: "Scheduled" },
              { value: "failed", label: "Failed" },
              { value: "not_started", label: "Not started" },
              { value: "opened", label: "Opened" },
            ]}
            className="w-full min-w-0 font-semibold text-dark-blue sm:!w-auto sm:min-w-[140px]"
          />
        </div>

        {campaignName ? (
          <p className="mb-4 break-words text-xs text-muted sm:text-sm">
            Showing {filteredRows.length} of {rows.length} prospects
            {rows.length ? (
              <>
                {" "}
                · Delivered {summary.delivered} · Opened {summary.opened} · Scheduled{" "}
                {summary.scheduled} · Failed {summary.failed} · Not started{" "}
                {summary.not_started}
              </>
            ) : null}
          </p>
        ) : null}

        <p className="mb-2 text-[10px] text-muted md:hidden">Swipe horizontally to see all columns.</p>
        <div className="overflow-hidden rounded-[10px] border border-line bg-white">
          <div className="-mx-px overflow-x-auto px-px [scrollbar-width:thin]">
            <table className="min-w-[640px] w-full border-separate border-spacing-0 text-left">
              <thead className="bg-white">
                <tr>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.08em] text-muted">
                    Prospect
                  </th>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.08em] text-muted">
                    Email
                  </th>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.08em] text-muted">
                    Status
                  </th>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.08em] text-muted">
                    Sent at
                  </th>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.08em] text-muted">
                    Opened
                  </th>
                  <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.08em] text-muted">
                    Failure reason
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted">
                      Loading email campaign stats…
                    </td>
                  </tr>
                )}

                {!loading && filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted">
                      {selectedCampaignId
                        ? "No prospects match this filter."
                        : "Choose a campaign to view email stats."}
                    </td>
                  </tr>
                )}

                {!loading &&
                  filteredRows.map((row) => (
                    <tr key={row.prospectId} className="border-t border-line align-top">
                      <td className="px-4 py-3 text-[12px] font-medium text-dark-blue">
                        {row.name}
                      </td>
                      <td className="px-4 py-3 text-[12px] font-medium text-dark-blue">
                        {row.email || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={[
                            "inline-flex items-center rounded-full px-2 py-1 text-[10px] font-semibold",
                            statusClass[row.status],
                          ].join(" ")}
                        >
                          {STATUS_LABEL[row.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[12px] text-dark-blue">
                        {formatSentAt(row.sentAt)}
                      </td>
                      <td className="px-4 py-3 text-[12px] text-dark-blue">
                        {row.status === "delivered"
                          ? formatOpened(row.emailOpened, row.emailOpenedAt)
                          : "—"}
                      </td>
                      <td className="max-w-md px-4 py-3 text-[12px] text-muted">
                        {row.status === "failed" && row.failureReason
                          ? row.failureReason
                          : "—"}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
